(() => {
  'use strict';
  const configNode = document.querySelector('[data-dftb-gift-config]');
  if (!configNode || window.dftbMysteryGift) return;
  const config = JSON.parse(configNode.textContent);
  const originalFetch = window.fetch.bind(window);
  const excluded = new Set(config.excludedProductIds.map(Number));
  const giftId = Number(config.giftProductId);
  const variantId = Number(config.giftVariantId);
  const threshold = config.threshold;
  const root = window.Shopify?.routes?.root || '/';
  const panels = () => [...document.querySelectorAll('[data-dftb-gift]')];
  let pending = false;
  let running = null;
  let timer;
  let previousProgress = null;
  let previousFree = false;
  let failureFingerprint = '';
  let allowSubmit = false;
  let cartDirty = false;
  let changing = 0;

  function eligibleTotal(cart) {
    return cart.items.reduce((sum, item) => excluded.has(Number(item.product_id)) || Number(item.product_id) === giftId ? sum : sum + item.final_line_price, 0);
  }
  function gifts(cart) { return cart.items.filter(item => Number(item.product_id) === giftId); }
  function managed(cart) { return gifts(cart).filter(item => item.properties?._dftb_gift === '75'); }
  function fingerprint(cart) {
    return JSON.stringify([cart.currency, cart.items.filter(item => Number(item.product_id) !== giftId).map(item => [item.key, item.quantity, item.final_line_price]), cart.discount_codes]);
  }
  function money(cents) {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
  }
  async function request(path, payload) {
    const response = await originalFetch(root + path, payload ? { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(payload) } : { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
    const result = await response.json();
    if (!response.ok || result.status >= 400) throw new Error(result.description || result.message || 'Cart request failed');
    return result;
  }
  async function change(key, quantity) {
    const cart = await request('cart/change.js', { id: key, quantity });
    cartDirty = true;
    return cart;
  }
  function render(cart, error = '') {
    const eligible = eligibleTotal(cart);
    const supported = cart.currency === 'GBP';
    const progress = Math.min(100, eligible / threshold * 100);
    const free = gifts(cart).some(item => item.final_line_price === 0 && item.quantity === 1) && eligible >= threshold;
    const moving = previousProgress !== null && previousProgress !== progress;
    const celebrate = free && !previousFree;
    for (const panel of panels()) {
      panel.hidden = !supported;
      if (!supported) continue;
      panel.style.setProperty('--gift-progress', progress + '%');
      const bar = panel.querySelector('[role="progressbar"]');
      bar.setAttribute('aria-valuenow', Math.min(75, eligible / 100).toFixed(2));
      bar.setAttribute('aria-valuetext', money(eligible) + ' of £75');
      const label = panel.querySelector('[data-gift-message]');
      label.textContent = free ? config.strings.added : eligible >= threshold ? config.strings.unlocked : config.strings.remaining.replace('__AMOUNT__', money(threshold - eligible));
      const errorNode = panel.querySelector('[data-gift-error]');
      errorNode.textContent = error;
      errorNode.hidden = !error;
      if (moving) { panel.classList.remove('is-moving'); void panel.offsetWidth; panel.classList.add('is-moving'); setTimeout(() => panel.classList.remove('is-moving'), 950); }
      if (celebrate) { panel.classList.remove('is-celebrating'); void panel.offsetWidth; panel.classList.add('is-celebrating'); setTimeout(() => panel.classList.remove('is-celebrating'), 2400); }
    }
    previousProgress = progress;
    previousFree = free;
  }
  async function reconcile() {
    let cart = await request('cart.js');
    let eligible = eligibleTotal(cart) >= threshold && cart.currency === 'GBP';
    let own = managed(cart);
    const otherGift = gifts(cart).some(item => item.properties?._dftb_gift !== '75');
    for (const item of own.slice(eligible && !otherGift ? 1 : 0)) cart = await change(item.key, 0);
    own = managed(cart);
    if (own[0]?.quantity > 1) cart = await change(own[0].key, 1);
    eligible = eligibleTotal(cart) >= threshold && cart.currency === 'GBP';
    const stamp = fingerprint(cart);
    if (eligible && !gifts(cart).length && failureFingerprint !== stamp) {
      if (config.previewDiscount) {
        const codes = (cart.discount_codes || []).map(code => code.code);
        if (!codes.includes(config.previewDiscount)) {
          cart = await request('cart/update.js', { discount: [...codes, config.previewDiscount].join(',') });
          cartDirty = true;
        }
      }
      try {
        await request('cart/add.js', { items: [{ id: variantId, quantity: 1, properties: { _dftb_gift: '75' } }] });
        cartDirty = true;
        cart = await request('cart.js');
        const added = managed(cart)[0];
        if (!added || added.final_line_price !== 0 || eligibleTotal(cart) < threshold) {
          for (const item of managed(cart)) cart = await change(item.key, 0);
          failureFingerprint = fingerprint(cart);
          render(cart, config.strings.unavailable);
          return cart;
        }
        failureFingerprint = '';
      } catch (error) {
        cart = await request('cart.js');
        for (const item of managed(cart).filter(item => item.final_line_price !== 0)) cart = await change(item.key, 0);
        failureFingerprint = fingerprint(cart);
        render(cart, config.strings.unavailable);
        return cart;
      }
    }
    for (const item of managed(cart).filter(item => item.final_line_price !== 0)) {
      cart = await change(item.key, 0);
      failureFingerprint = fingerprint(cart);
    }
    render(cart, eligible && failureFingerprint === fingerprint(cart) ? config.strings.unavailable : '');
    return cart;
  }
  function refreshMarkup() {
    if (!cartDirty) return;
    cartDirty = false;
    document.dispatchEvent(new CustomEvent('cart:build'));
  }
  function sync() {
    pending = true;
    if (running) return running;
    const job = async () => {
      let cart;
      try {
        while (pending) {
          pending = false;
          if (changing) { pending = true; await new Promise(resolve => setTimeout(resolve, 80)); continue; }
          cart = await reconcile();
        }
        refreshMarkup();
        return cart;
      } finally { running = null; }
    };
    running = navigator.locks ? navigator.locks.request('dftb-free-tee-cart', job) : job();
    return running;
  }
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(() => sync().catch(() => {
      for (const panel of panels()) { const el = panel.querySelector('[data-gift-error]'); el.textContent = config.strings.retry; el.hidden = false; }
    }), 80);
  }
  window.fetch = async function(input, options) {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url || '';
    const mutation = /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(url);
    if (mutation) changing++;
    try { return await originalFetch(input, options); }
    finally { if (mutation) { changing--; schedule(); } }
  };
  for (const event of ['ajaxProduct:added', 'cart:updated', 'cart:open', 'drawerOpen', 'shopify:section:load']) document.addEventListener(event, schedule);
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!form.matches('#CartDrawerForm, #CartPageForm') || allowSubmit) return;
    if (event.submitter && event.submitter.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const button = event.submitter || form.querySelector('[name="checkout"]');
    if (button) button.disabled = true;
    try {
      await sync();
      allowSubmit = true;
      if (button) button.disabled = false;
      form.requestSubmit(button || undefined);
    } catch (error) {
      for (const panel of panels()) { const el = panel.querySelector('[data-gift-error]'); el.textContent = config.strings.retry; el.hidden = false; }
    } finally { allowSubmit = false; if (button) { button.disabled = false; button.classList.remove('btn--loading'); } }
  }, true);
  window.dftbMysteryGift = { sync, eligibleTotal };
  schedule();
})();
